/** URL navigation and the remembered Local Map representation. */
import type { LocalMapPerspective, SpatialLayout, ViewName } from "../types";
import { decompressSnapshot, compressSnapshot } from "./compressed-url-state";
import { commitUrl } from "./history";
import { readPerspective, readSpatialLayout, readView } from "./navigation";

/** The Local Map keeps its established `view=local` address. */
const viewNameToUrl = (name: ViewName): string => name === "map" ? "local" : name;

/** Navigation read from an address. An omitted perspective outside Local Map uses the saved preference. */
export interface InitialUrlState {
  view: ViewName;
  perspective: LocalMapPerspective | null;
  /** The explicitly named 3D layout; when omitted, the remembered choice or bundle's default supplies it. */
  spatialLayout: SpatialLayout | null;
  nodeId: string | null;
  openDirectories: ReadonlySet<string>;
  hasUrlState: boolean;
}

/** Parse an address without consulting browser storage; an explicit Local Map link owns its representation. */
export function parseNavigation(search: string): InitialUrlState {
  const params = new URLSearchParams(search);
  const compressed = params.get("s");
  if (compressed) {
    const snapshot = decompressSnapshot(compressed);
    return { view: snapshot.view, perspective: snapshot.perspective, spatialLayout: snapshot.spatialLayout, nodeId: snapshot.selectedNodeId,
      openDirectories: snapshot.openDirectories, hasUrlState: true };
  }
  const view = params.get("view");
  const node = params.get("node");
  const directory = params.get("dir");
  const mode = params.get("mode");
  const layout = params.get("layout");
  const hasUrlState = view !== null || node !== null || directory !== null || mode !== null || layout !== null;
  const local = hasUrlState && readView(view) === "map";
  return {
    view: readView(view),
    perspective: local || mode !== null ? readPerspective(mode, view) : null,
    // Existing subject/directory links named a Connections scope before layout was explicit. Preserve those places;
    // only a fresh arrival with no requested subject chooses the bundle's entry-aware default.
    spatialLayout: layout !== null ? readSpatialLayout(layout)
      : view === "graph" || view === "force" || node !== null || directory !== null ? "connections" : null,
    nodeId: node,
    openDirectories: new Set(directory !== null ? [directory.replace(/\/+$/u, "")] : []),
    hasUrlState
  };
}

/** URL navigation takes precedence over saved navigation. A board chooses World Map on a fresh visit. */
export const parseInitialState = (): InitialUrlState => parseNavigation(window.location.search);

/** Write view, subject, representation and 3D layout together, preserving paths and exploration state. */
export function updateUrlState(view: ViewName, nodeId: string | null, perspective: LocalMapPerspective, spatialLayout: SpatialLayout): void {
  const url = new URL(window.location.href);
  const params = url.searchParams;
  const compressed = params.get("s");
  if (compressed) {
    params.set("s", compressSnapshot({ ...decompressSnapshot(compressed), view, perspective, spatialLayout, selectedNodeId: nodeId }));
  }
  params.set("view", viewNameToUrl(view));
  params.set("mode", perspective);
  params.set("layout", spatialLayout);
  if (nodeId) params.set("node", nodeId);
  else params.delete("node");
  commitUrl(`${url.pathname}?${params.toString()}`);
}
